import { useEffect, useRef, useState } from 'react';

const concepts = [
  {
    term: 'Cartografía del Alma',
    desc: 'Un mapa sistémico que revela los patrones ocultos de tu experiencia, trazando con precisión los nodos de tensión y los caminos de liberación.',
  },
  {
    term: 'Alineación Nodal',
    desc: 'El proceso de sincronizar los puntos de tensión de tu sistema con su estado de coherencia natural, activando el flujo de Ayni (reciprocidad).',
  },
  {
    term: 'Re-ingeniería Espiritual',
    desc: 'Una metodología rigurosa que aplica principios sistémicos y de geometría sagrada para reparar, optimizar y expandir tu arquitectura interior.',
  },
];

export default function ManifiestoSection() {
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="manifiesto"
      ref={sectionRef}
      className="bg-[#2A2D34] py-32 px-6 overflow-hidden"
    >
      <div className="max-w-screen-xl mx-auto">
        {/* Label */}
        <div className="flex items-center gap-3 mb-16">
          <span className="border border-[#8B9D83]/40 text-[#8B9D83] text-[10px] uppercase tracking-[3px] px-4 py-1.5 rounded-sm">
            ( El Blueprint )
          </span>
          <span className="text-[#F8F6F3]/20 text-xs">—— 01</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div
            className={`transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h2 className="mb-8 leading-[1.1]">
              <span className="block text-[#F8F6F3] font-bold" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
                La Nube Geodésica
              </span>
              <span className="block text-[#8B9D83]/70 font-light" style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>
                no es una
              </span>
              <span className="block text-[#C97B63] font-bold" style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>
                metáfora. ⊹
              </span>
            </h2>

            <p className="text-[#F8F6F3]/70 text-base md:text-lg leading-relaxed max-w-lg mb-10">
              Es una estructura real. Una arquitectura viva que conecta los puntos dispersos de
              la experiencia humana —traumas, creencias, relaciones, entorno— en un sistema coherente
              y expandible. Cada nodo es una parte de ti. Cada arista es una relación sistémica.
              Juntos forman tu <strong className="text-[#8B9D83]">Bio-Santuario</strong> personal.
            </p>

            {/* Concepts accordion */}
            <div className="space-y-3">
              {concepts.map((c, i) => (
                <div
                  key={c.term}
                  className={`border rounded-lg overflow-hidden transition-all duration-300 cursor-pointer ${
                    activeIndex === i
                      ? 'border-[#C97B63]/50 bg-[#C97B63]/5'
                      : 'border-[#F8F6F3]/10 bg-transparent hover:border-[#8B9D83]/30'
                  }`}
                  onClick={() => setActiveIndex(i)}
                >
                  <div className="flex items-center justify-between px-5 py-4">
                    <span className="text-[#F8F6F3] font-medium text-sm tracking-wide">{c.term}</span>
                    <i
                      className={`ri-arrow-right-s-line text-lg transition-transform duration-300 ${
                        activeIndex === i ? 'rotate-90 text-[#C97B63]' : 'text-[#F8F6F3]/40'
                      }`}
                    />
                  </div>
                  {activeIndex === i && (
                    <div className="px-5 pb-4 text-[#F8F6F3]/60 text-sm leading-relaxed border-t border-[#F8F6F3]/5">
                      <p className="pt-3">{c.desc}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <a
              href="#cartografia"
              onClick={(e) => { e.preventDefault(); document.querySelector('#cartografia')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-2 text-[#C97B63] text-sm mt-8 hover:gap-3 transition-all duration-300 cursor-pointer"
            >
              Explorar la Geometría Sagrada <i className="ri-arrow-right-line" />
            </a>
          </div>

          {/* Right: Image + stats */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ height: '520px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/3656d61cdc86ec0ceb214dde16f1bcd3.jpg"
                alt="Cartografía del Alma"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A2D34]/80 via-transparent to-transparent" />
            </div>

            {/* Floating metric cards */}
            <div className="absolute -bottom-6 -left-6 bg-[#1A1C20] border border-[#8B9D83]/30 rounded-xl p-5">
              <div className="text-[#8B9D83] font-bold leading-none mb-1" style={{ fontSize: '3rem' }}>1,200+</div>
              <div className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">Nodos Activos</div>
            </div>

            <div className="absolute -top-4 -right-4 bg-[#1A1C20] border border-[#C97B63]/30 rounded-xl p-5">
              <div className="text-[#C97B63] font-bold leading-none mb-1" style={{ fontSize: '3rem' }}>6</div>
              <div className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">Pilares Dimensionales</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
